import { zodResolver } from "@hookform/resolvers/zod";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { AlertCircle, Eye, EyeOff, LoaderCircle, LogIn } from "lucide-react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { FieldError } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ApiError } from "@/lib/api";
import { type MeResponse, useLogin } from "@/lib/auth";
import { AuthShell } from "./AuthShell";

const loginSchema = z.object({
  nombre_usuario: z.string().trim().min(1, "Introduce tu usuario"),
  password: z.string().min(1, "Introduce tu contraseña"),
});

type LoginFormValues = z.infer<typeof loginSchema>;

// sanitizeRedirect evita open-redirects: solo se aceptan rutas internas absolutas.
function sanitizeRedirect(target: string | undefined): string {
  if (target?.startsWith("/") && !target.startsWith("//")) {
    return target;
  }
  return "/";
}

// Primer destino tras el login según el estado de onboarding (clave → NDA → app).
function destinoTrasLogin(me: MeResponse, redirect: string | undefined): string {
  if (me.debe_cambiar_password) {
    return "/mi-clave";
  }
  if (me.nda_pendiente) {
    return "/nda";
  }
  return sanitizeRedirect(redirect);
}

// Mapea los códigos de error del callback SSO (?sso_error=<code>) a mensajes en
// español. El callback de Microsoft devuelve al usuario al login con estos códigos
// cuando el flujo falla (los define el backend en internal/auth/entra.go).
function mensajeDeErrorSSO(code: string): string {
  switch (code) {
    case "USUARIO_NO_PROVISIONADO":
      return "Tu cuenta de Microsoft no está dada de alta en la aplicación. Contacta con tu administrador.";
    case "USUARIO_INACTIVO":
      return "Tu cuenta está desactivada. Contacta con tu administrador.";
    case "INVITACION_CADUCADA":
      // #239 (D2): el SSO completa la invitación solo si sigue viva; sin ella no entra.
      return "Tu invitación ha caducado o se ha revocado. Pide una nueva al administrador.";
    case "SSO_PROVEEDOR":
      return "Microsoft canceló o rechazó el acceso. Inténtalo de nuevo.";
    case "SSO_NO_CONFIGURADO":
      return "El acceso con Microsoft no está disponible en este momento.";
    default:
      return "No se pudo completar el acceso con Microsoft. Inténtalo de nuevo.";
  }
}

function mensajeDeError(err: unknown): string {
  if (err instanceof ApiError) {
    switch (err.code) {
      case "CREDENCIALES_INVALIDAS":
        return "Usuario o contraseña incorrectos.";
      case "CAMPOS_REQUERIDOS":
        return "Introduce tu usuario y tu contraseña.";
      case "RATE_LIMIT":
        return err.retryAfter
          ? `Demasiados intentos. Inténtalo de nuevo en ${err.retryAfter} segundos.`
          : "Demasiados intentos. Espera un momento e inténtalo de nuevo.";
      default:
        return err.message || "No se pudo iniciar sesión.";
    }
  }
  return "No se pudo iniciar sesión. Comprueba tu conexión e inténtalo de nuevo.";
}

export function LoginPage() {
  const navigate = useNavigate();
  const { redirect, sso_error } = useSearch({ from: "/login" });
  const login = useLogin();
  // Si el callback SSO nos devolvió con ?sso_error, se muestra ese mensaje al montar.
  const [formError, setFormError] = useState<string | null>(
    sso_error ? mensajeDeErrorSSO(sso_error) : null,
  );
  const [mostrarPassword, setMostrarPassword] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormValues>({
    resolver: zodResolver(loginSchema),
    defaultValues: { nombre_usuario: "", password: "" },
  });

  const onSubmit = handleSubmit(async (values) => {
    setFormError(null);
    try {
      const me = await login.mutateAsync(values);
      await navigate({ to: destinoTrasLogin(me, redirect) });
    } catch (err) {
      setFormError(mensajeDeError(err));
    }
  });

  return (
    <AuthShell>
      <Card>
        <CardHeader>
          <CardTitle>Iniciar sesión</CardTitle>
          <CardDescription>Accede con tu usuario de LOTO System.</CardDescription>
        </CardHeader>
        <CardContent>
          {formError ? (
            <Alert variant="destructive">
              <AlertCircle className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" />
              <span>{formError}</span>
            </Alert>
          ) : null}

          <form className="flex flex-col gap-4" onSubmit={onSubmit} noValidate>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="nombre_usuario">Usuario</Label>
              <Input
                id="nombre_usuario"
                autoComplete="username"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                aria-invalid={errors.nombre_usuario ? true : undefined}
                {...register("nombre_usuario")}
              />
              <FieldError>{errors.nombre_usuario?.message}</FieldError>
            </div>

            <div className="flex flex-col gap-1.5">
              <Label htmlFor="password">Contraseña</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={mostrarPassword ? "text" : "password"}
                  autoComplete="current-password"
                  className="pr-12"
                  aria-invalid={errors.password ? true : undefined}
                  {...register("password")}
                />
                <button
                  type="button"
                  onClick={() => setMostrarPassword((v) => !v)}
                  className="absolute inset-y-0 right-0 flex w-12 items-center justify-center text-slate-500 hover:text-slate-700"
                  aria-label={mostrarPassword ? "Ocultar contraseña" : "Mostrar contraseña"}
                >
                  {mostrarPassword ? (
                    <EyeOff className="h-5 w-5" aria-hidden="true" />
                  ) : (
                    <Eye className="h-5 w-5" aria-hidden="true" />
                  )}
                </button>
              </div>
              <FieldError>{errors.password?.message}</FieldError>
            </div>

            <Button type="submit" size="lg" disabled={login.isPending} className="mt-2 w-full">
              {login.isPending ? (
                <LoaderCircle className="h-5 w-5 animate-spin" aria-hidden="true" />
              ) : (
                <LogIn className="h-5 w-5" aria-hidden="true" />
              )}
              Entrar
            </Button>
          </form>

          {/* Acceso alternativo por SSO corporativo (Microsoft Entra ID, #91). Es una
              navegación de página completa (no fetch): la inicia el backend en
              GET /api/v1/auth/microsoft/login. */}
          <div className="relative my-4" aria-hidden="true">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-slate-200 border-t" />
            </div>
            <div className="relative flex justify-center">
              <span className="bg-white px-2 text-slate-500 text-sm">o</span>
            </div>
          </div>

          <Button asChild variant="outline" size="lg" className="w-full">
            <a href="/api/v1/auth/microsoft/login">
              <svg viewBox="0 0 21 21" className="h-5 w-5" aria-hidden="true">
                <rect x="1" y="1" width="9" height="9" fill="#f25022" />
                <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
                <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
                <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
              </svg>
              Entrar con Microsoft
            </a>
          </Button>

          {/* Recuperación autoservicio (#198): abre el flujo público /recuperar → correo
              → /restablecer. El SSO Microsoft gestiona su clave aparte. */}
          <p className="mt-4 text-center text-sm text-slate-500">
            <Link to="/recuperar" className="font-medium text-primary-700 hover:underline">
              ¿Has olvidado tu contraseña?
            </Link>
          </p>
        </CardContent>
      </Card>
    </AuthShell>
  );
}
